/**
 * @fileOverview the main UI component
 */

import React, { useMemo, useState } from "react";

import Sidebar from "./ui/Sidebar";
import AppBar from "@mui/material/AppBar";
import CssBaseline from "@material-ui/core/CssBaseline";
import Divider from "@material-ui/core/Divider";
import Drawer from "@material-ui/core/Drawer";
import IconButton from "@material-ui/core/IconButton";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Stack from "@mui/material/Stack";

import { makeTheme, sidebarStyles } from "./ui/styles";
import { ThemeProvider, responsiveFontSizes } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import "./App.scss";
import { GlobalProvider } from "./common/GlobalContext";

import MapLayout from "./map/MapLayout";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";

import ShapefileDownloadDialog from "./ui/ShapefileDownloadDialog";
import WhatsNewDialog from "./ui/WhatsNewDialog";
import ContactUsDialog from "./ui/ContactUsDialog";
import DataLayout from "./table/DataLayout";

import Box from "@mui/material/Box";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import { styled } from "@mui/material/styles";

import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";

const drawerWidth = 250;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
    paddingLeft: 0,
    marginRight: 0,
    paddingRight: 0,
    marginTop: 50,
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 255,
    }),
    overflow: "hidden",
  })
);

function App() {
  // UI Settings
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const modePC = prefersDarkMode ? "dark" : "light";
  const [mode, setMode] = useState(modePC);
  useMemo(() => {
    setMode(modePC);
  }, [modePC]);

  let theme = makeTheme(mode);
  theme = responsiveFontSizes(theme);
  const classes = sidebarStyles();

  const [whatsNewOpen, setWhatsNewOpen] = React.useState(false);
  const handleClickWhatsNew = () => {
    setWhatsNewOpen(true);
  };
  const [shapefileDownloadOpen, setShapefileDownloadOpen] =
    React.useState(false);
  const handleClickOpenShapefiles = () => {
    setShapefileDownloadOpen(true);
  };
  const [contactUsOpen, setContactUsOpen] = React.useState(false);
  const handleClickContactUs = () => {
    setContactUsOpen(true);
  };

  const [openDrawer, setOpenDrawer] = React.useState(true);

  const handleDrawerOpen = () => {
    setOpenDrawer(true);
  };

  const handleDrawerClose = () => {
    setOpenDrawer(false);
  };
  document.body.style.overflow = "hidden";

  const handleChangeDarkMode = (event) => {
    if (event.target.checked) setMode("dark");
    else setMode("light");
  };

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.root}>
        {whatsNewOpen ? (
          <WhatsNewDialog
            open={whatsNewOpen}
            setOpen={setWhatsNewOpen}
            mode={mode}
          />
        ) : null}
        {shapefileDownloadOpen ? (
          <ShapefileDownloadDialog
            shapefileDownloadOpen={shapefileDownloadOpen}
            setShapefileDownloadOpen={setShapefileDownloadOpen}
          />
        ) : null}
        {contactUsOpen ? (
          <ContactUsDialog
            open={contactUsOpen}
            setOpen={setContactUsOpen}
            mode={mode}
          />
        ) : null}

        <CssBaseline />
        <AppBar
          position="fixed"
          open={openDrawer}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "#1E1E1E",
            mb: 0,
            pb: 0,
          }}
          className={classes.appBar}
        >
          <Toolbar sx={{ marginBottom: 0 }} variant="dense">
            <Grid container spacing={0}>
              <Grid item xs={1}>
                <img
                  className="logo"
                  src="logo-light.svg"
                  alt="Maryland DOT Logo"
                  title="Maryland DOT Logo"
                />
              </Grid>
              <Grid item xs={1}>
                <img
                  className="logo-tti"
                  src="TTI-white.svg"
                  alt="TTI Logo"
                  title="TTI Logo"
                />
              </Grid>
              <Grid
                item
                xs={6}
                container
                alignItems="center"
                justifyContent="center"
              >
                <Typography variant="h4" component="h1" noWrap>
                  Maryland Roadway Performance Tool
                </Typography>
              </Grid>

              <Grid
                item
                xs={4}
                style={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Show What's New">
                    <Button
                      onClick={() => {
                        handleClickWhatsNew();
                      }}
                      sx={{ fontSize: 12, color: "white" }}
                    >
                      What's New?
                    </Button>
                  </Tooltip>
                  <Tooltip title="User's Guide">
                    <Button
                      sx={{ fontSize: 12, color: "white" }}
                      href={
                        process.env.PUBLIC_URL +
                        "Maryland Roadway Performance Tool - User Guide.pdf"
                      }
                      target="_blank"
                    >
                      User's Guide
                    </Button>
                  </Tooltip>
                  <Tooltip title="Download Shapefiles">
                    <Button
                      onClick={() => {
                        handleClickOpenShapefiles();
                      }}
                      sx={{ fontSize: 12, color: "white" }}
                    >
                      Download Shapefiles
                    </Button>
                  </Tooltip>
                  <Tooltip title="Contact Us">
                    <Button
                      onClick={() => {
                        handleClickContactUs();
                      }}
                      sx={{ fontSize: 12, color: "white" }}
                    >
                      Contact Us
                    </Button>
                  </Tooltip>
                </Stack>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
        {!openDrawer && (
          <Tooltip title="Open Settings / Filters" placement="right">
            <IconButton
              aria-label="open settings and filters"
              onClick={handleDrawerOpen}
              sx={{
                position: "fixed",
                top: 56,
                left: 8,
                zIndex: (theme) => theme.zIndex.drawer + 2,
                backgroundColor: "background.paper",
                color: mode === "dark" ? "#fff" : "text.primary",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: 3,
                borderRadius: 1,
                "&:hover": { backgroundColor: "background.paper" },
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        )}
        <GlobalProvider>
          <Drawer
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                width: drawerWidth,
                boxSizing: "border-box",
              },
            }}
            variant="persistent"
            anchor="left"
            open={openDrawer}
          >
            <IconButton onClick={handleDrawerClose} aria-label="Previous">
              {theme.direction === "ltr" ? (
                <ChevronLeftIcon />
              ) : (
                <ChevronRightIcon />
              )}
            </IconButton>
            <Box
              sx={{
                marginTop: 0,
                paddingTop: 0,
                marginBottom: 0,
                paddingBottom: 0,
              }}
              display="flex"
              justifyContent="flex-end"
              alignItems="flex-end"
            >
              <Stack direction="column" spacing={0}>
                <Tooltip title="Hide Settings">
                  <IconButton
                    onClick={handleDrawerClose}
                    sx={{
                      marginTop: 0,
                      paddingTop: 0,
                      marginBottom: 0,
                      paddingBottom: 0,
                    }}
                  >
                    <Typography
                      noWrap
                      variant="subtitle2"
                      sx={{
                        marginTop: 0,
                        paddingTop: 0,
                        marginBottom: 0,
                        paddingBottom: 0,
                      }}
                    >
                      Hide Settings
                    </Typography>
                    <CloseIcon
                      sx={{
                        marginBottom: 0,
                        marginLeft: 1,
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Toggle Dark Mode">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={mode === "dark" ? true : false}
                        onChange={handleChangeDarkMode}
                        inputProps={{ "aria-label": "controlled" }}
                      />
                    }
                    label="Dark Mode"
                  />
                </Tooltip>
              </Stack>
            </Box>

            <Divider />
            <Sidebar mode={mode} />
          </Drawer>
          <Main
            open={openDrawer}
            sx={{ overflow: "hidden", maxWidth: "100vw", mt: 3 }}
          >
            <Box sx={{ overflow: "hidden", maxWidth: "100vw", mt: 0, pt: 0 }}>
              <MapLayout mode={mode} openDrawer={openDrawer} />
            </Box>
            <Box
              style={{
                maxHeight: "45vh",
                overflow: "auto",
                maxWidth: "100vw",
              }}
            >
              <DataLayout mode={mode} />
            </Box>
          </Main>
        </GlobalProvider>
      </div>
    </ThemeProvider>
  );
}

export default App;